/**
 * Crypto primitives for arcano.app
 * This code is under copyright Arcano Gmbh (https://arcano.app).
 *
 * - no commercial use
 * - no modifications
 * - no redistribution
 *
 * without written consent of Arcano Gmbh, zentralstrasse 47, 8003 zürich, switzerland, https://arcano.app
*/
'use strict';


import {groupBy, mfetch} from "./utils";
// @ts-ignore
import scrypt from "scrypt-async";


/**
 * This creates the algorithm description for symmetric (=file) encryption and decryption
 *
 * @param counter   Initalization vector: Uint8Array of random data
 */
export function getSymmetricAlgorithm(counter: Uint8Array): {name: string, length: number, iv: Uint8Array} {
    return {
        name: "AES-GCM",
        iv: counter,
        length: 256
    };
}

/**
 * Wrapper for encrypting blob data (such as file chunks coming from file.slice)
 *
 * @param blob      Data to encrypt
 * @param key       The symmetric encryption key
 * @param algorithm The algorithm, @see getSymmetricAlgorithm
 */
export async function encrypt(blob: Blob, key: CryptoKey, algorithm: Algorithm): Promise<ArrayBuffer> {
    // const buffer = await blob.arrayBuffer();
    const buffer = await new Response(blob).arrayBuffer();
    return await encryptMessage(buffer, key, algorithm);
}


/**
 * Get the encoded message, encrypt it and display a representation
 * of the ciphertext in the "Ciphertext" element.
 */
export async function encryptMessage(inputText: ArrayBuffer, key: CryptoKey, algorithm: Algorithm): Promise<ArrayBuffer> {
    if(inputText.byteLength === 0) {
        return new ArrayBuffer(0)  // this is for Safari and empty inputText
    }
    const ciphertext = await window.crypto.subtle.encrypt(
        algorithm,
        key,
        inputText
    );
    return ciphertext
}

/**
 */
export function convertStringToArrayBufferView(str: string): Uint8Array {
    let bytes = new Uint8Array(str.length);
    for (let iii = 0; iii < str.length; iii++) {
        bytes[iii] = str.charCodeAt(iii);
    }
    return bytes;
}



interface RsaAlgorithm {
    publicExponent: Uint8Array,
    modulusLength: number,
    name: string,
    hash: {
        name: string
    }
}

/**
 * Returns the algorithm for RSA Public Key operations
 */
export function getRsaAlgorithm(): RsaAlgorithm {
    // noinspection MagicNumberJS
    return {
        name: "RSA-OAEP",
        modulusLength: 4096, //can be 1024, 2048, or 4096
        publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
        hash: {
            name: "SHA-256"
        }, //can be "SHA-1", "SHA-256", "SHA-384", or "SHA-512"
    }
}

/**
 * Generates a RSA crypto pair (private and public key)
 */
function generatePublicAndPrivateKey(rsaAlgo: RsaAlgorithm): Promise<CryptoKeyPair> {
    return window.crypto.subtle.generateKey(rsaAlgo,
        true, //whether the key is extractable (i.e. can be used in exportKey)
        ["encrypt", "decrypt"] //must be ["encrypt", "decrypt"] or ["wrapKey", "unwrapKey"]
    );
}


/**
 *
 */
export async function getMatchingFileName(hash: string, encryptedFilename: string):  Promise<string> {
    console.log('REMOVE')

    if (hash[0] === '#') {
        hash = hash.slice(1);  // remove preceeding #
    }

    if(encryptedFilename.length === 0) {
        return ""
    }

    const split = hash.split('-');

    const counter = new Uint8Array(atob(split[0]).split(",").map(x => parseInt(x)));

    const keydata = JSON.parse(atob(split[1]));

    const algorithm = getSymmetricAlgorithm(counter);

    let key = await window.crypto.subtle.importKey('jwk',
        keydata,
        algorithm,
        false,
        keydata.key_ops
    );

    const sencryptedFilename = atob(encryptedFilename);

    let arraybuffer = convertStringToArrayBufferView(sencryptedFilename);

    const decryptedFilename = await window.crypto.subtle.decrypt(
                    algorithm,
                    key,
                    arraybuffer
             );
    return new TextDecoder("utf-8").decode(decryptedFilename);
}

/**
 */
function uint8ArrayToHex(uint8: Uint8Array|ArrayBuffer): string {
    let arr = Array.from(new Uint8Array(uint8))
    return [...arr].map (b => (b as number).toString(16).padStart(2, "0")).join ("")
}

/**
 */
function hexToUint8Array(hex: string): Uint8Array {
    const match = hex.match(/.{1,2}/g);
    const numbers = match!.map(byte => parseInt(byte, 16));
    return new Uint8Array(numbers);
}

interface ScryptHash {
    hkdfSaltHex: string,
    scryptSaltHex: string,
    scryptHashedPassword: string
}

/**
 * Generates a scrypt hash
 */
export async function generateScryptHash(passwordData: Uint8Array,
                                         hkdfSalt?: string,
                                         scryptSalt?: string): Promise<ScryptHash> {

    let hkdfSaltArray: Uint8Array, scryptSaltArray: Uint8Array;

    if(!hkdfSalt || hkdfSalt.length === 0) {
        hkdfSaltArray = window.crypto.getRandomValues(new Uint8Array(16));
    } else {
        hkdfSaltArray = hexToUint8Array(hkdfSalt)
    }
    if(!scryptSalt || scryptSalt.length === 0) {
        scryptSaltArray = window.crypto.getRandomValues(new Uint8Array(16));
    } else {
        scryptSaltArray = hexToUint8Array(scryptSalt)
    }

    const hkdfKey = await window.crypto.subtle.importKey(
        "raw",
        passwordData,
        {name: "HKDF"},
        false,
        ["deriveBits", "deriveKey"]
    );

    const hkdfBits = await window.crypto.subtle.deriveBits(
        {name: 'HKDF', hash: 'SHA-256', salt: hkdfSaltArray, info: new Uint8Array()},
        hkdfKey,
        256
    );

    const scryptHashedPassword: string = await new Promise((resolve, reject) => {
        try {
            // @ts-ignore
            scrypt(uint8ArrayToHex(hkdfBits), scryptSaltArray, {
                    logN: 16,
                    r: 8,
                    p: 1,
                    dkLen: 256,
                    interruptStep: 0,  //0-8
                    encoding: 'hex'
                },
                function (res: string) {

                    resolve(res)
                });
        } catch (e) {
            reject(e)
        }
    })
    const hkdfSaltHex = uint8ArrayToHex(hkdfSaltArray)
    const scryptSaltHex = uint8ArrayToHex(scryptSaltArray)
    return {hkdfSaltHex, scryptSaltHex, scryptHashedPassword};
}


export interface UserData {
    hashedPassword: ArrayBuffer,
    iv: Uint8Array,
    salt: Uint8Array,
    privateKey: ArrayBuffer,
    publicKey: string,
    hkdfSalt: string,
    scryptSalt: string,
    scryptHashedPassword: string
}
/**
 * Creates the relevant crypto data for a new user
 */
export async function addNewUser(password: string): Promise<UserData> {
    const rsaAlgo = getRsaAlgorithm();
    const key = await generatePublicAndPrivateKey(rsaAlgo);
    const publicKeyhold = await window.crypto.subtle.exportKey("jwk", key.publicKey!);
    const publicKeyJson = JSON.stringify(publicKeyhold);  // note: this can be stored in the database

    // noinspection MagicNumberJS
    const iv = window.crypto.getRandomValues(new Uint8Array(16));
    // noinspection MagicNumberJS
    const salt = window.crypto.getRandomValues(new Uint8Array(16));

    if (password === undefined) {
        password = window.prompt("Enter your password")!; // todo replace with nice prompt
    }

    const enc = new TextEncoder();
    const passwordData = enc.encode(password);
    // only the hashedPassword is sent to the server so that it never has the actual password used for the privateKey
    // it is used as a conventional password and is hashed (again - with a salt) on the server
    // this is required to prevent the server ever having access to the password used for privateKey encryption
    const hashedPassword = await crypto.subtle.digest('SHA-256', passwordData);

    const {hkdfSaltHex, scryptSaltHex, scryptHashedPassword} = await generateScryptHash(passwordData);

    const passwordKey = await window.crypto.subtle.importKey(
        "raw",
        passwordData,
        {name: "PBKDF2"},
        false,
        ["deriveBits", "deriveKey"]
    );

    const wrappingKey = await window.crypto.subtle.deriveKey(
        {
            "name": "PBKDF2",
            salt: salt,
            "iterations": 100000,
            "hash": "SHA-256"
        },
        passwordKey,
        {"name": "AES-GCM", "iv": iv, "length": 256} as AesDerivedKeyParams,
        true,
        ["wrapKey", "unwrapKey"]
    );

    const wrappedKey = await window.crypto.subtle.wrapKey(
        "pkcs8",
        key.privateKey!,
        wrappingKey,
        {
            name: "AES-GCM",
            iv: iv
        });
    return {
        hashedPassword: hashedPassword,
        iv: iv,
        salt: salt,
        privateKey: wrappedKey,
        publicKey: publicKeyJson,
        hkdfSalt: hkdfSaltHex,
        scryptSalt: scryptSaltHex,
        scryptHashedPassword: scryptHashedPassword
    }
}

/**
 * This is called when the user logs in, the actual password is reaplced by its hashed version
 */
export function sendEditedFormData(pathname: string, data: FormData, method?: string, callback?: (req: XMLHttpRequest, res: any) => any ) {
    let request = new XMLHttpRequest();

    if (!callback) {
        callback = function () {
            window.location.href = request.response;
        };
    }
    if (!method) {
        method = 'POST'
    }

    request.onload = function() {
        // calls callback with request and response (=this)
        if(callback) {
            callback(request, this)
        }
    };
    request.open(method, pathname);
    request.send(data);
}


/**
 * update the status text
 */
export function updateProgressStatus(key: string, urls: any, nChunks: number, size: any) {

    const [prefix, name] = key.split('-');
    const transferItem = document.querySelector(`.transfer-file-list-item[data-prefix="${prefix}"][data-name="${name}"]`)
    if(!transferItem) {
        return
    }
    let progressBar = transferItem.querySelector('.bottom-progress-bar');
    if(!progressBar) {
        progressBar = document.createElement('div')
        progressBar.classList.add('bottom-progress-bar', 'd-none')
        transferItem.appendChild(progressBar);
    }

    const button = transferItem.querySelector('.download-button') as HTMLElement;
    const spinner = button.querySelector('.fas');
    if(!spinner) {
        button.style.cursor = 'default';
        const spinner = document.createElement('span')
        button.appendChild(spinner)
        // spinner.classList.add('fas', 'fa-spinner', 'fa-spin', 'fa-2x', 'download-spinner');  # todo for old v1?

    }
    const icon = button.querySelector('.uppy-Dashboard-Item-previewIcon') as HTMLElement;
    if(icon) {
        icon.style.display = 'none';
    }

    const percentage = Math.max(1, Math.round(100 - urls.length / nChunks * 100.0));
    (progressBar as HTMLElement).style.backgroundImage = `linear-gradient(to right, rgb(61,117,208) ${percentage}%, white ${percentage}% 100%)`
    progressBar.setAttribute('data-percentage', percentage.toString());
    progressBar.setAttribute('data-size', size.toString());
    progressBar.classList.remove('d-none')

    if(percentage >= 100.0) {
        progressBar.remove();
        if(spinner) {
            spinner.remove()
        }
        button.style.cursor = 'cursor';
        icon.style.display = 'block';
    }
}




/**
 * Hashes the filename
 */
export function randomHash(): string {
    let random = window.crypto.getRandomValues(new Uint8Array(16));
    const randomName = Array.from(random).map(b => b.toString(16).padStart(2, '0')).join('')
    return randomName

    // const enc = new TextEncoder();
    // const filenameData = enc.encode(filename);
    // const hashFilenameBuffer = await crypto.subtle.digest('SHA-256', filenameData);
    // const hashFilenameArray = Array.from(new Uint8Array(hashFilenameBuffer));
    // return hashFilenameArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Gets the secret key given the user password
 */
export async function getSecretKey(password: string): Promise<CryptoKey|undefined> {
    const response = await mfetch('/private_key');
    const binary = await response.arrayBuffer();
    let userData;
    if (response.status === 200) {
        userData = JSON.parse(String.fromCharCode.apply(null, Array.from(new Uint8Array(binary))));  // type: {*}
    } else {
        throw Error(response.status.toString())
    }

    const salt = new Uint8Array(Object.values(userData.salt));
    const iv = new Uint8Array(Object.values(userData.iv));
    const privateKey = new Uint8Array(Object.values(userData.privateKey));
    const rsaAlgo = getRsaAlgorithm();
    const enc = new TextEncoder();

    // noinspection JSCheckFunctionSignatures
    const passwordKey = await window.crypto.subtle.importKey(
        "raw",
        enc.encode(password),
        {name: "PBKDF2"},
        false,
        ["deriveBits", "deriveKey"]
    );

    const wrappingKey = await window.crypto.subtle.deriveKey(
        {
            "name": "PBKDF2",
            salt: salt,
            "iterations": 100000,
            "hash": "SHA-256"
        },
        passwordKey,
        {"name": "AES-GCM", "iv": iv, "length": 256} as AesDerivedKeyParams,
        true,
        ["wrapKey", "unwrapKey"]
    );

    try {
        const unwrappedKey = await crypto.subtle.unwrapKey("pkcs8",
            privateKey,
            wrappingKey, {
                name: "AES-GCM",
                iv: iv
            }, rsaAlgo, false, ['decrypt']);
        return unwrappedKey;
    } catch (e) {
        return undefined
    }
}

/**
 * Decrypts with the key from indexedDB
 */
export async function extractSecretUsingLocalSecretKey(ciphertext: string,
                                                       unwrappedKey: CryptoKey): Promise<string> {
    const ivElem = document.getElementById('iv') as HTMLInputElement
    const val = ivElem.value.split(',').map(x => parseInt(x))
    const iv = new Uint8Array(Object.values(val));

    const decrypted = await window.crypto.subtle.decrypt({
            name: "RSA-OAEP",
            iv: iv
        },
        unwrappedKey, //from generateKey or importKey above
        convertStringToArrayBufferView(atob(ciphertext))
    );
    return new TextDecoder("utf-8").decode(decrypted);
}

/**
 *
 */
export async function prepareFileSharing(files: Array<File>,
                                  secret: string,
                                  userData: UserData,
                                  key: CryptoKey,
                                  publicKey: CryptoKey): Promise<{}> {

    const groupedFileByPrefix = groupBy(files, (x: any) => x.prefix)

    const sharedFiles: any = {}

    for (let prefix in groupedFileByPrefix) {
        // check if the property/key is defined in the object itself, not in parent
        // if (dictionary.hasOwnProperty(key)) {
        //     console.log(key, dictionary[key]);
        const filesForPrefix = groupedFileByPrefix[prefix]

        const newPrefix = await fetch('/file_prefix/uuid').then(response => response.text())


        for (let i = 0; i < filesForPrefix.length; i++) {
            const file = filesForPrefix[i];
            if (!secret) {
                secret = await extractSecretUsingLocalSecretKey(file.encryptedkey, key)
            }

            // const regex = /\?prefix=([a-zA-z0-9]+)#/
            // const prefix = secret.match(regex)[1]
            const newSecret = secret.replace('?prefix=' + prefix, '?prefix=' + newPrefix)

            const encryptedSecret = await window.crypto.subtle.encrypt({
                    name: "RSA-OAEP",
                    iv: userData.iv,
                },
                publicKey, //from generateKey or importKey above
                convertStringToArrayBufferView(newSecret)
            )
            sharedFiles[file.file_id] = {secret: new Uint8Array(encryptedSecret), prefix: newPrefix}
        }
    }
    return sharedFiles;
}

export async function decryptSymmetricKey(hash: string) {
    if (hash[0] === '#') {
        hash = hash.slice(1);  // remove preceeding #
    }
    const split = hash.split('-');

    const counter = new Uint8Array(atob(split[0]).split(",").map(x => parseInt(x)));

    const keydata = JSON.parse(atob(split[1]));

    const algorithm = getSymmetricAlgorithm(counter);

    let key = await window.crypto.subtle.importKey('jwk',
        keydata,
        algorithm,
        false,
        keydata.key_ops
    );
    return {algorithm, key}
}